import { useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import {
  AlertTriangle,
  Ban,
  CloudOff,
  Lock,
  RefreshCw,
  SearchX,
  ServerCrash,
  TimerOff,
  WifiOff,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { classifyError, type ClassifiedError, type ErrorKind } from '@/lib/errorKind'

/** Visual weight. `inline` sits inside a card, `section` replaces a table, `page` owns the viewport. */
export type ErrorStateSize = 'inline' | 'section' | 'page'

export interface ErrorStateProps {
  /** The raw thrown value. Classified internally so call sites stay one line. */
  error: unknown
  /** Overrides the classifier when the caller knows better, e.g. 'crash' inside a boundary. */
  fallbackKind?: ErrorKind
  /** Replaces the classifier's headline. Use for context the classifier can't know. */
  title?: string
  /** Replaces the classifier's body copy. */
  description?: string
  size?: ErrorStateSize
  /** Wiring this enables the retry button. Return a promise to get the spinning state. */
  onRetry?: () => void | Promise<unknown>
  /** Rendered beside retry — a "Go to dashboard" link, a "Sign in" button. */
  action?: React.ReactNode
  className?: string
}

const KIND_ICONS: Record<ErrorKind, LucideIcon> = {
  offline: WifiOff,
  network: CloudOff,
  timeout: TimerOff,
  unauthorized: Lock,
  forbidden: Ban,
  notFound: SearchX,
  conflict: AlertTriangle,
  validation: AlertTriangle,
  rateLimited: TimerOff,
  server: ServerCrash,
  crash: AlertTriangle,
}

/**
 * Kinds the user caused or cannot act on read as neutral, not alarming. Reserving
 * red for genuine faults keeps it meaningful when it does appear.
 */
const NEUTRAL_KINDS: ReadonlySet<ErrorKind> = new Set<ErrorKind>([
  'forbidden',
  'notFound',
  'unauthorized',
  'validation',
])

const SIZE_STYLES: Record<
  ErrorStateSize,
  { root: string; icon: string; glyph: string; title: string; body: string; gap: string }
> = {
  inline: {
    root: 'gap-3 rounded-xl px-4 py-4 text-left sm:flex-row sm:items-start',
    icon: 'h-9 w-9 rounded-lg',
    glyph: 'h-[18px] w-[18px]',
    title: 'text-sm font-semibold',
    body: 'text-sm',
    gap: 'mt-3',
  },
  section: {
    root: 'items-center gap-4 rounded-2xl px-6 py-10 text-center',
    icon: 'h-12 w-12 rounded-xl',
    glyph: 'h-6 w-6',
    title: 'text-base font-semibold',
    body: 'text-sm',
    gap: 'mt-5',
  },
  page: {
    root: 'items-center gap-5 rounded-2xl px-6 py-14 text-center',
    icon: 'h-16 w-16 rounded-2xl',
    glyph: 'h-8 w-8',
    title: 'text-xl font-semibold tracking-[-0.01em]',
    body: 'text-sm',
    gap: 'mt-6',
  },
}

/**
 * The single presentation for every failure in the app: a classified headline,
 * a recovery sentence, and the actions that can actually fix it.
 *
 * Raw exception text is never the headline — it appears only as collapsed
 * supporting detail, so users read the problem and developers keep the specifics.
 */
export function ErrorState({
  error,
  fallbackKind = 'network',
  title,
  description,
  size = 'section',
  onRetry,
  action,
  className,
}: ErrorStateProps): React.ReactElement {
  const [isRetrying, setIsRetrying] = useState(false)
  const [showDetail, setShowDetail] = useState(false)

  const classified: ClassifiedError = classifyError(error, fallbackKind)
  const Icon = KIND_ICONS[classified.kind]
  const styles = SIZE_STYLES[size]
  const isNeutral = NEUTRAL_KINDS.has(classified.kind)
  const canRetry = Boolean(onRetry) && classified.retryable

  const handleRetry = async (): Promise<void> => {
    if (!onRetry || isRetrying) return
    setIsRetrying(true)
    try {
      await onRetry()
    } finally {
      setIsRetrying(false)
    }
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        'flex flex-col border',
        isNeutral
          ? 'border-gray-200 bg-gray-50/60 dark:border-gray-700 dark:bg-gray-800/40'
          : 'border-danger/20 bg-danger/[0.04] dark:border-danger/25 dark:bg-danger/[0.07]',
        styles.root,
        className
      )}
    >
      <span
        aria-hidden
        className={cn(
          'flex shrink-0 items-center justify-center',
          styles.icon,
          isNeutral
            ? 'bg-gray-100 text-gray-500 dark:bg-gray-700/60 dark:text-gray-400'
            : 'bg-danger/10 text-danger dark:bg-danger/15'
        )}
      >
        <Icon className={styles.glyph} strokeWidth={1.75} />
      </span>

      <div className={cn('min-w-0', size === 'inline' ? 'flex-1' : 'max-w-md')}>
        <p className={cn(styles.title, 'text-gray-900 dark:text-gray-100')}>
          {title ?? classified.title}
        </p>
        <p className={cn(styles.body, 'mt-1 text-gray-600 dark:text-gray-400')}>
          {description ?? classified.description}
        </p>

        {(canRetry || action) && (
          <div
            className={cn(
              'flex flex-wrap items-center gap-2',
              styles.gap,
              size !== 'inline' && 'justify-center'
            )}
          >
            {canRetry && (
              <button
                type="button"
                onClick={() => void handleRetry()}
                disabled={isRetrying}
                className="inline-flex h-8 min-h-8 cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-primary px-3 text-sm font-medium text-white transition-[background-color,scale] duration-150 hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 active:scale-[0.96] disabled:pointer-events-none disabled:opacity-60"
              >
                <RefreshCw
                  className={cn('h-3.5 w-3.5', isRetrying && 'animate-spin')}
                  strokeWidth={2}
                  aria-hidden
                />
                {isRetrying ? 'Retrying…' : 'Try again'}
              </button>
            )}
            {action}
          </div>
        )}

        {classified.detail && (
          <div className={cn('mt-3', size !== 'inline' && 'flex flex-col items-center')}>
            <button
              type="button"
              onClick={() => setShowDetail((open) => !open)}
              aria-expanded={showDetail}
              className="cursor-pointer rounded text-xs font-medium text-gray-500 underline decoration-gray-300 underline-offset-4 transition-colors hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 dark:text-gray-400 dark:decoration-gray-600 dark:hover:text-gray-200"
            >
              {showDetail ? 'Hide technical details' : 'Show technical details'}
            </button>
            {showDetail && (
              <p className="mt-2 max-w-full rounded-lg bg-gray-100/80 px-3 py-2 text-left font-mono text-xs leading-relaxed break-words text-gray-600 dark:bg-gray-900/50 dark:text-gray-400">
                {classified.status ? `${classified.status} — ` : ''}
                {classified.detail}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

ErrorState.displayName = 'ErrorState'
